/**
 * useAudioRecorder Hook
 * Low-level audio recording with silence detection.
 */

import { useState, useRef, useCallback, useEffect } from "react";
import type { AudioRecorderConfig } from "../types/voice";

const DEFAULT_SILENCE_THRESHOLD_MS = 1000;
const DEFAULT_SILENCE_LEVEL = 0.15;
const DEFAULT_MIN_SPEECH_DURATION_MS = 500;

interface UseAudioRecorderReturn {
  isRecording: boolean;
  audioLevel: number;
  start: () => Promise<void>;
  stop: () => void;
  error: string | null;
}

export function useAudioRecorder(
  config: AudioRecorderConfig = {},
): UseAudioRecorderReturn {
  const {
    silenceThresholdMs = DEFAULT_SILENCE_THRESHOLD_MS,
    silenceLevel = DEFAULT_SILENCE_LEVEL,
    minSpeechDurationMs = DEFAULT_MIN_SPEECH_DURATION_MS,
    onAudioCaptured,
    onError,
  } = config;

  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const silenceStartRef = useRef<number | null>(null);
  const speechStartRef = useRef<number | null>(null);
  const stoppedRef = useRef(false);
  const callbackCalledRef = useRef(false);
  const processAudioRef = useRef<(() => void) | null>(null);

  const onAudioCapturedRef = useRef(onAudioCaptured);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onAudioCapturedRef.current = onAudioCaptured;
    onErrorRef.current = onError;
  }, [onAudioCaptured, onError]);

  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    mediaRecorderRef.current = null;
    analyserRef.current = null;
    chunksRef.current = [];
    setAudioLevel(0);
  }, []);

  useEffect(() => {
    processAudioRef.current = () => {
      if (!analyserRef.current || stoppedRef.current) return;

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalized = dataArray[i] / 255;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      setAudioLevel(rms);

      const now = Date.now();

      if (rms > silenceLevel) {
        silenceStartRef.current = null;
        if (!speechStartRef.current) {
          speechStartRef.current = now;
        }
      } else {
        if (!silenceStartRef.current) {
          silenceStartRef.current = now;
        }

        const speechDuration = speechStartRef.current
          ? now - speechStartRef.current
          : 0;
        const silenceDuration = now - silenceStartRef.current;

        if (
          speechDuration >= minSpeechDurationMs &&
          silenceDuration >= silenceThresholdMs
        ) {
          if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state === "recording"
          ) {
            mediaRecorderRef.current.stop();
            return;
          }
        }
      }

      animationFrameRef.current = requestAnimationFrame(() => processAudioRef.current?.());
    };
  }, [silenceLevel, silenceThresholdMs, minSpeechDurationMs]);

  const start = useCallback(async () => {
    try {
      setError(null);
      stoppedRef.current = false;
      callbackCalledRef.current = false;
      chunksRef.current = [];
      silenceStartRef.current = null;
      speechStartRef.current = null;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";

      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const speechDuration = speechStartRef.current
          ? Date.now() - speechStartRef.current
          : 0;

        if (
          chunksRef.current.length > 0 &&
          speechDuration >= minSpeechDurationMs &&
          !callbackCalledRef.current
        ) {
          const blob = new Blob(chunksRef.current, { type: mimeType });
          if (blob.size > 1000) {
            callbackCalledRef.current = true;
            onAudioCapturedRef.current?.(blob);
          }
        }

        chunksRef.current = [];
        speechStartRef.current = null;
        silenceStartRef.current = null;

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        mediaRecorderRef.current = null;
        analyserRef.current = null;

        if (!stoppedRef.current) {
          setIsRecording(false);
        }
      };

      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      processAudioRef.current?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to start recording";
      setError(message);
      onErrorRef.current?.(err instanceof Error ? err : new Error(message));
      cleanup();
    }
  }, [minSpeechDurationMs, cleanup]);

  const stop = useCallback(() => {
    stoppedRef.current = true;
    setIsRecording(false);
    cleanup();
  }, [cleanup]);

  useEffect(() => {
    return () => {
      stoppedRef.current = true;
      cleanup();
    };
  }, [cleanup]);

  return { isRecording, audioLevel, start, stop, error };
}
